<script lang="ts" setup>
import { EffectFade, Autoplay, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import SwiperCore from 'swiper'
import 'swiper/css'
import 'swiper/css/effect-fade'
import Pagination2 from '@/components/Slider/Pagination2.vue'
import type { Image } from '@/utils/types'

const props = defineProps({
  images: {
    type: Array as PropType<Image[]>,
    default: () => []
  },
  title: {
    type: String,
    default: ''
  },
  intro: {
    type: Array as PropType<{
      title: string
      text: string
    }[]>,
    default: () => []
  }
})

SwiperCore.use([Autoplay, Pagination])

const currentIndex: Ref<number> = ref(0)

const swiperSlideSelector = ref()
const swiperSlideSelectorMobile = ref()
const onSwiper = (data: any) => {
  //存放 swiper
  swiperSlideSelector.value = data
}

const onSwiperMobile = (data: any) => {
  //存放 swiper
  swiperSlideSelectorMobile.value = data
}

const swiperTo = (index: number) => {
  swiperSlideSelector.value.slideTo(index)
  swiperSlideSelectorMobile.value.slideTo(index)
}

const handelSwiperChange = (data: any) => {
  currentIndex.value = data.realIndex
  // swiperSlideSecondSelector.value.slideTo(data.realIndex)
}

const handleSlidePrev = () => {
  swiperSlideSelector.value.slidePrev()
  swiperSlideSelectorMobile.value.slidePrev()
}
const handleSlideNext = () => {
  swiperSlideSelector.value.slideNext()
  swiperSlideSelectorMobile.value.slideNext()
}

const handlePaginationChange = (index: number) => {
  swiperTo(index)
  currentIndex.value = index
}

const products = computed(() => props.images.map((image) => ({
  image: image,
  title: props.title,
  intro: props.intro,
})))

</script>

<template>
  <div class="slide">
    <swiper :autoplay="{
      delay: 3000
    }" :slides-per-view="1" :space-between="0" :speed="300" :loop="true" :modules="[EffectFade]" effect="fade"
      class="swiper-list pc" @swiper="onSwiper" @slide-change="handelSwiperChange">
      <swiper-slide v-for="(item, index) in products" :key="index">
        <div class="swiper-list__box">
          <figure class="swiper-list__pic square__pic">
            <Nimg :src="item.image?.link" :alt="item.image?.alt" :title="item.image?.title" />
          </figure>
          <div class="swiper-list__right pc">
            <h1 class="swiper-list__title">{{ item.title }}</h1>
            <ul class="swiper-list__intro">
              <template v-for="(x, index) in item.intro" :key="index">
                <li v-if="!isEmpty(x.text)">
                  <div class="swiper-list__intro__title">{{ x.title }}</div>
                  <div class="swiper-list__intro__text">{{ x.text }}</div>
                </li>
              </template>
            </ul>
          </div>
        </div>
      </swiper-slide>
    </swiper>
    <div class="slide__function">
      <div class="pagination-box" v-if="products.length > 1">
        <Pagination2 :currentIndex="currentIndex" @update:change="handlePaginationChange" :totalIndex="products.length"
          class="pagination-box__item" />
      </div>
      <div class="slide__arrow" v-if="products.length > 1">
        <div class="arrow brand4-1">
          <div class="arrow__prev" @click="handleSlidePrev">
            <svg-icon name="icon-arrow-right2" />
          </div>
          <div class="arrow__next" @click="handleSlideNext">
            <svg-icon name="icon-arrow-right2" />
          </div>
        </div>
      </div>
    </div>
    <swiper :autoplay="{
      delay: 3000
    }" :slides-per-view="1" :space-between="0" :speed="300" :loop="true" :modules="[EffectFade]" effect="fade"
      class="swiper-list mobile" @swiper="onSwiperMobile" @slide-change="handelSwiperChange">
      <swiper-slide v-for="(item, index) in products" :key="index">
        <div class="swiper-list__box">
          <div class="swiper-list__right mobile">
            <h1 class="swiper-list__title">{{ item.title }}</h1>
            <ul class="swiper-list__intro">
              <li v-for="(x, index) in item.intro" :key="index">
                <div class="swiper-list__intro__title">{{ x.title }}</div>
                <div class="swiper-list__intro__text">{{ x.text }}</div>
              </li>
            </ul>
          </div>
        </div>
      </swiper-slide>
    </swiper>
  </div>
</template>

<style lang="scss" scoped>
.swiper-slide {
  opacity: 0 !important;
}

.swiper-slide-active {
  opacity: 1 !important;
}

.slide {
  position: relative;
  margin-bottom: toRem(32);

  &__function {
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 1;
    width: 52%;
    height: toRem(65);
    padding-left: toRem(24);

    @include max-media(767) {
      position: relative;
      width: 100%;
      padding-left: 0;
      bottom: auto;
      margin-top: toRem(24);
    }
  }

  &__arrow {
    display: flex;
    justify-content: flex-start;

    .arrow {
      margin-top: toRem(12);
    }
  }
}

.swiper-list {
  &.mobile {
    @include min-media(768) {
      display: none;
    }
  }

  &__box {
    display: flex;

    @include max-media(767) {
      flex-direction: column;
    }
  }

  &__pic {
    width: 48%;
    padding-top: 48%;

    @include max-media(767) {
      width: 100%;
      padding-top: 100%;
    }
  }

  &__right {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    width: 52%;
    padding-left: toRem(24);
    padding-bottom: calc(toRem(65) + toRem(32));

    &.pc {
      @include max-media(767) {
        display: none;
      }
    }

    &.mobile {
      @include min-media(768) {
        display: none;
      }
    }

    @include max-media(767) {
      width: 100%;
      padding-left: 0;
      padding-bottom: 0;
    }
  }

  &__title {
    margin-bottom: toRem(32);
    font-size: toRem(28);
    font-weight: 500;
    line-height: 1.3;

    @include max-media(767) {
      margin-bottom: toRem(24);
    }
  }

  &__intro {
    display: flex;
    flex-wrap: wrap;

    li {
      display: flex;
      flex-wrap: wrap;
      font-size: toRem(16);
      font-weight: 400;
      line-height: 1.45;
      margin-bottom: toRem(6);
      margin-left: toRem(6);
      width: 100%;

      // &:nth-child(1),
      // &:nth-child(2),
      // &:nth-child(3) {
      //   width: 100%;
      // }
    }

    &__title {
      color: $color-brand-4_1;
      margin-right: toRem(8);
    }

    &__text {
      color: $color-gray-1;
    }
  }
}
</style>
